import { useState } from "react";
import type { FC, MouseEvent } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import { colours, device } from "@helpers";
import {
	helpsiteTrackButton,
	helpsiteTrackCta,
} from "@helpers/helpsite-mixpanel";
import Icon from "@components/Icon";

const MobileSupportFooterContainer = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	box-sizing: border-box;
	overflow-x: hidden;

	@media (min-width: ${device.laptop}) {
		display: none;
	}
`;

const SupportSection = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const SupportCTAContainer = styled.div`
	background-color: ${colours.PALE_PEACH};
	padding: 30px 15px 40px;
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	box-sizing: border-box;
`;

const SupportContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 30px;
	width: 100%;
	max-width: 395px;
	box-sizing: border-box;
`;

const SupportHeader = styled.div`
	display: flex;
	flex-direction: column;
	gap: 18px;
`;

const SupportTitle = styled.h2`
	font-family: var(--taviraj-light);
	font-weight: 500;
	font-size: 30px;
	line-height: 1.7060000101725261em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
`;

const SupportSubtitle = styled.h3`
	font-family: var(--euclid-medium);
	font-weight: 500;
	font-size: 20px;
	line-height: 1.268000030517578em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
`;

const ContactTimes = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0;
	width: 100%;
	max-width: 365px;
	box-sizing: border-box;
`;

const ContactTimesText = styled.p`
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.2680000066757202em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
`;

const SupportTimesLink = styled.a`
	color: ${colours.MIDNIGHT_BLUE};
	text-decoration: underline;
	cursor: pointer;
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.2680000066757202em;
	pointer-events: auto;
	position: relative;
	z-index: 1;

	&:hover {
		text-decoration: underline;
		opacity: 0.8;
	}

	&:visited {
		color: ${colours.MIDNIGHT_BLUE};
	}

	&:active {
		opacity: 0.6;
	}
`;

const ExpandedSupportTimes = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	max-width: 365px;
	box-sizing: border-box;
	margin-top: 10px;
	background-color: ${colours.WHITE};
	position: relative;
	padding: 15px;
	border-radius: 8px;

	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(255, 235, 231, 0.5);
		border-radius: 8px;
		pointer-events: none;
		z-index: 0;
	}

	& > * {
		position: relative;
		z-index: 1;
	}
`;

const ExpandedSupportTimesText = styled.p`
	font-family: var(--euclid-regular);
	font-size: 16px;
	line-height: 1.2680000066757202em;
	color: ${colours.MIDNIGHT_BLUE};
	margin: 0;
`;

const ChatButtonContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`;

const ChatButton = styled.button`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 6px;
	padding: 5px 20px;
	background-color: ${colours.MIDNIGHT_BLUE};
	border-radius: 12px;
	border: 2px solid transparent;
	cursor: pointer;
	width: 100%;
	box-sizing: border-box;
	transition: background-color 0.2s;

	&:hover {
		background-color: ${colours.WHITE};
		border-color: ${colours.MIDNIGHT_BLUE};
	}
`;

const ChatButtonText = styled.span`
	font-family: var(--euclid-regular);
	font-size: 18px;
	line-height: 1.2679999669392903em;
	color: ${colours.WHITE};
	transition: color 0.2s;

	${ChatButton}:hover & {
		color: ${colours.MIDNIGHT_BLUE};
	}
`;

const ChatButtonIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	
	img {
		transition: filter 0.2s;
		/* Make icon white initially */
		filter: brightness(0) invert(1);
	}
	
	${ChatButton}:hover & img {
		/* Make icon midnight blue on hover */
		filter: brightness(0) saturate(100%) invert(15%) sepia(20%) saturate(2000%) hue-rotate(200deg) brightness(0.9);
	}
`;

type FreshchatWidget = { show: () => void; open: () => void };

const HelpMobileSupportFooter: FC = () => {
	const router = useRouter();
	const pagePath = router.asPath.split("?")[0];
	const [showSupportTimes, setShowSupportTimes] = useState(false);

	const handleSupportTimesClick = (e: MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setShowSupportTimes((prev) => {
			const next = !prev;
			helpsiteTrackButton({
				helpsite_section: "footer_mobile",
				page_path: pagePath,
				label: "Support times",
				cta_type: next ? "expand" : "collapse",
			});
			return next;
		});
	};

	const handleChatButtonClick = () => {
		helpsiteTrackCta({
			helpsite_section: "footer_mobile",
			page_path: pagePath,
			label: "Chat with us in felix app",
			cta_type: "freshchat_open",
		});
		if (typeof window !== "undefined") {
			const fcWidget = (window as Window & { fcWidget?: FreshchatWidget })
				.fcWidget;
			if (fcWidget) {
				fcWidget.show();
				fcWidget.open();
			}
		}
	};

	return (
		<MobileSupportFooterContainer>
			<SupportSection>
				<SupportCTAContainer>
					<SupportContentWrapper>
						<SupportHeader>
							<SupportTitle>Need more help? </SupportTitle>
							<SupportSubtitle>Chat with us</SupportSubtitle>
							<ContactTimes>
								<ContactTimesText>
									Our Australian based support team is online until 8PM today
									(Sydney time).{" "}
									<SupportTimesLink href="#" onClick={handleSupportTimesClick}>
										Support times
									</SupportTimesLink>
								</ContactTimesText>
								{showSupportTimes && (
									<ExpandedSupportTimes>
										<ExpandedSupportTimesText>
											Our Australian based support team is online 7 days 8am-8pm
											(Sydney time).
										</ExpandedSupportTimesText>
									</ExpandedSupportTimes>
								)}
							</ContactTimes>
						</SupportHeader>
						<ChatButtonContainer>
							<ChatButton onClick={handleChatButtonClick}>
								<ChatButtonIcon>
									<Icon
										iconName="chat"
										iconSizes={{
											iconMobile: 40,
											iconTablet: 40,
											iconLaptop: 40,
											iconDesktop: 40,
											iconDesktopLarge: 40,
										}}
										alt="Chat icon"
									/>
								</ChatButtonIcon>
								<ChatButtonText>Chat with us in felix app</ChatButtonText>
							</ChatButton>
						</ChatButtonContainer>
					</SupportContentWrapper>
				</SupportCTAContainer>
				{/* <FeedbackSection>
					<FeedbackContent>
						<FeedbackTitle>Got ideas or suggestions?</FeedbackTitle>
						<FeedbackText>
							We'd love to hear them, <br />
							leave your feedback here
						</FeedbackText>
					</FeedbackContent>
				</FeedbackSection> */}
			</SupportSection>
		</MobileSupportFooterContainer>
	);
};

export default HelpMobileSupportFooter;
